import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, X } from 'lucide-react';
import { AttributeType, ValueDataType, type AttributeToken, type ProductFilterToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MultiSelect } from '~/components/multi-select';
import { useAttributesList } from '~/hooks/use-attributes';
import { useAttributeValuesList } from '~/hooks/use-attribute-values';
import { useProductFilterMutations } from '~/hooks/use-product-filters';
import { useStatusesList } from '~/hooks/use-statuses';
import { serverMessage } from '~/lib/api/server-error';
import { parseAssignments, serializeAssignments, type AssignmentsDraft, type AttributeAssignmentDraft } from '~/lib/filters/assignments';

interface Option {
	id: number;
	nm: string;
}

/** Attributes, statuses given to the products the filter matches (and taken back when they stop matching). */
export function AssignmentsDialog({ filter, onClose }: { filter: ProductFilterToken | null; onClose: () => void }) {
	return (
		<Dialog open={Boolean(filter)} onOpenChange={open => !open && onClose()}>
			<DialogContent className="max-h-[88vh] gap-0 overflow-hidden p-0 sm:max-w-3xl">
				{/* Keyed body, so each opened filter starts from its own saved assignments. */}
				{filter && <AssignmentsBody key={filter.id} filter={filter} onClose={onClose} />}
			</DialogContent>
		</Dialog>
	);
}

function AssignmentsBody({ filter, onClose }: { filter: ProductFilterToken; onClose: () => void }) {
	const { t } = useTranslation();
	const { setAssignments } = useProductFilterMutations();
	const statuses = useStatusesList();
	// DateTime attributes cannot be assigned (the server drops them).
	const attributes = (useAttributesList().data ?? []).filter(a => a.vt !== ValueDataType.DateTime);
	const [draft, setDraft] = useState<AssignmentsDraft>(() => parseAssignments(filter.Assignments));

	const statusOptions: Option[] = (statuses.data ?? []).filter(s => s.id != null).map(s => ({ id: s.id as number, nm: s.nm ?? '' }));
	const setRow = (index: number, row: AttributeAssignmentDraft) =>
		setDraft(d => ({ ...d, attributes: d.attributes.map((r, i) => (i === index ? row : r)) }));

	const save = () =>
		setAssignments.mutate(
			{ ...filter, Assignments: serializeAssignments(draft) ?? null },
			{
				onSuccess: () => {
					toast.success(t('common.save'));
					onClose();
				},
				onError: err => toast.error(serverMessage(err))
			}
		);

	return (
		<>
			<DialogHeader className="border-b border-border p-5">
				<DialogTitle>{t('catalog.filters.assignments.title', { name: filter.Name ?? '' })}</DialogTitle>
				<DialogDescription>
					{t('catalog.filters.assignments.subtitle')}
					{filter.ShouldRunManually ? ` ${t('catalog.filters.assignments.manualNote')}` : ''}
				</DialogDescription>
			</DialogHeader>
			<div className="flex max-h-[60vh] flex-col gap-5 overflow-auto p-5">
				<div className="flex flex-col gap-1.5">
					<Label>{t('catalog.filters.assignments.statuses')}</Label>
					<MultiSelect
						value={statusOptions.filter(o => draft.statusIds.includes(o.id))}
						onChange={next => setDraft(d => ({ ...d, statusIds: next.map(o => o.id) }))}
						options={statusOptions}
						getKey={o => String(o.id)}
						getLabel={o => o.nm}
						placeholder={t('catalog.filters.selectEntities')}
					/>
				</div>
				<div className="flex flex-col gap-2">
					<Label>{t('catalog.filters.assignments.attributes')}</Label>
					{draft.attributes.map((row, index) => (
						<AttributeRow
							key={index}
							row={row}
							attributes={attributes}
							onChange={next => setRow(index, next)}
							onRemove={() => setDraft(d => ({ ...d, attributes: d.attributes.filter((_, i) => i !== index) }))}
						/>
					))}
					<div>
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => setDraft(d => ({ ...d, attributes: [...d.attributes, { valueIds: [] }] }))}>
							<Plus size={14} />
							{t('catalog.filters.assignments.addAttribute')}
						</Button>
					</div>
				</div>
			</div>
			<DialogFooter className="border-t border-border p-4">
				<Button variant="ghost" onClick={onClose}>
					{t('common.cancel')}
				</Button>
				<Button onClick={save} disabled={setAssignments.isPending}>
					{t('common.save')}
				</Button>
			</DialogFooter>
		</>
	);
}

function AttributeRow({
	row,
	attributes,
	onChange,
	onRemove
}: {
	row: AttributeAssignmentDraft;
	attributes: AttributeToken[];
	onChange: (row: AttributeAssignmentDraft) => void;
	onRemove: () => void;
}) {
	const { t } = useTranslation();
	const attribute = attributes.find(a => a.id === row.attributeId);
	return (
		<div className="flex flex-wrap items-center gap-2">
			<Select
				value={row.attributeId != null ? String(row.attributeId) : undefined}
				onValueChange={id => onChange({ attributeId: Number(id), valueIds: [] })}>
				<SelectTrigger className="h-9 w-56">
					<SelectValue placeholder={t('catalog.filters.assignments.attribute')} />
				</SelectTrigger>
				<SelectContent>
					{attributes.map(a => (
						<SelectItem key={a.id} value={String(a.id)}>
							{a.nm}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			{attribute && <AttributeValueEditor attribute={attribute} row={row} onChange={onChange} />}
			<Button
				type="button"
				variant="ghost"
				size="icon"
				className="h-7 w-7 text-danger-fg"
				onClick={onRemove}
				aria-label={t('common.remove')}>
				<X size={14} />
			</Button>
		</div>
	);
}

function AttributeValueEditor({
	attribute,
	row,
	onChange
}: {
	attribute: AttributeToken;
	row: AttributeAssignmentDraft;
	onChange: (row: AttributeAssignmentDraft) => void;
}) {
	const { t } = useTranslation();
	const isList = attribute.at === AttributeType.ListOfValues;
	const values = useAttributeValuesList(isList ? attribute.id : undefined);

	if (isList) {
		const options: Option[] = (values.data ?? [])
			.filter(v => v.id != null)
			.map(v => ({ id: v.id as number, nm: v.vtxt ?? v.cd ?? String(v.id) }));
		return (
			<div className="min-w-52 flex-1">
				<MultiSelect
					value={options.filter(o => row.valueIds.includes(o.id))}
					onChange={next => onChange({ ...row, valueIds: next.map(o => o.id), value: undefined })}
					options={options}
					getKey={o => String(o.id)}
					getLabel={o => o.nm}
					placeholder={t('catalog.filters.selectEntities')}
				/>
			</div>
		);
	}

	if (attribute.vt === ValueDataType.Boolean)
		return (
			<Select
				value={row.value != null ? String(row.value) : undefined}
				onValueChange={v => onChange({ ...row, valueIds: [], value: Number(v) })}>
				<SelectTrigger className="h-9 w-32">
					<SelectValue placeholder={t('catalog.filters.value')} />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="1">{t('common.yes')}</SelectItem>
					<SelectItem value="0">{t('common.no')}</SelectItem>
				</SelectContent>
			</Select>
		);

	const numeric = attribute.vt === ValueDataType.Integer || attribute.vt === ValueDataType.Decimal;
	return (
		<Input
			className="h-9 w-48"
			type={numeric ? 'number' : 'text'}
			step={attribute.vt === ValueDataType.Decimal ? 'any' : '1'}
			placeholder={t('catalog.filters.value')}
			value={row.value ?? ''}
			onChange={e => {
				const raw = e.target.value;
				onChange({ ...row, valueIds: [], value: numeric && raw !== '' ? Number(raw) : raw });
			}}
		/>
	);
}
